import axios from 'axios';

// Determine the API URL based on environment
const isDevelopment = import.meta.env.MODE === 'development';
export const API_URL = isDevelopment 
  ? import.meta.env.VITE_API_URL 
  : import.meta.env.VITE_PRODUCTION_API_URL;

// Export API endpoints
export const API_ENDPOINTS = {
  login: '/api/login',
  checkAuth: '/api/check-auth',
  inventory: {
    bySku: '/api/inventory/sku',
    byLot: '/api/inventory/lots'
  },
  orders: {
    list: '/api/orders',
    details: (id) => `/api/orders/${id}`,
    shippingRate: (id) => `/api/orders/${id}/shipping-rate`
  },
  shipping: '/api/shipping',
  customers: '/api/customers'
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add request interceptor for debugging
api.interceptors.request.use(
  config => {
    const fullUrl = `${config.baseURL}${config.url}`;
    console.log('API Request:', {
      url: fullUrl,
      method: config.method,
      headers: config.headers
    });
    return config;
  },
  error => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Response Error:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data
    });
    
    // Don't redirect to login if we're already on the login page or checking auth
    const isLoginRequest = error.config?.url === API_ENDPOINTS.login;
    const isAuthCheckRequest = error.config?.url === API_ENDPOINTS.checkAuth;
    
    if (error.response?.status === 401 && !isLoginRequest && !isAuthCheckRequest) {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Inventory API functions
export const getInventoryByLot = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.inventory.byLot);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory by lot:', error);
    throw error;
  }
};

export const getInventoryBySKU = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.inventory.bySku);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory by SKU:', error);
    throw error;
  }
};

// Orders API functions
export const getOrders = async (startDate, endDate) => {
  try {
    const response = await api.get(API_ENDPOINTS.orders.list, {
      params: { startDate, endDate }
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const getOrderDetails = async (orderId) => {
  try {
    const response = await api.get(API_ENDPOINTS.orders.details(orderId));
    return response.data;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

export const updateOrderShippingRate = async (orderId, shippingData) => {
  try {
    const response = await api.put(API_ENDPOINTS.orders.shippingRate(orderId), shippingData);
    return response.data;
  } catch (error) {
    console.error('Error updating shipping rate:', error);
    throw error;
  }
};

export default api; 